/*
 * <lh-accordion-item> — one question/answer pair.
 *
 *   <lh-accordion-item [open]>
 *     <button data-accordion-toggle aria-expanded="false" aria-controls="panel-id">…</button>
 *     <div data-accordion-panel id="panel-id" hidden>…</div>
 *   </lh-accordion-item>
 *
 * Height animation follows the recipe in docs/animations.md: measure, animate
 * between explicit values, then release back to auto so the panel keeps
 * reflowing with its content.
 */

(() => {

const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';
const DURATION = 500; // --lh-duration-slow
const EASING = 'cubic-bezier(0.75, 0, 0.175, 1)'; // --lh-ease

class AccordionItem extends HTMLElement {
  #toggle = null;
  #panel = null;
  #animation = null;

  connectedCallback() {
    this.#toggle = this.querySelector('[data-accordion-toggle]');
    this.#panel = this.querySelector('[data-accordion-panel]');
    if (!this.#toggle || !this.#panel) return;

    this.#toggle.addEventListener('click', this.#onClick);
    this.#sync(this.open, false);
  }

  disconnectedCallback() {
    this.#toggle?.removeEventListener('click', this.#onClick);
    this.#animation?.cancel();
  }

  get open() {
    return this.hasAttribute('open');
  }

  set open(value) {
    this.toggleAttribute('open', Boolean(value));
    this.#sync(Boolean(value), true);
  }

  #onClick = () => {
    this.open = !this.open;
  };

  #sync(open, animate) {
    this.#toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    this.#animation?.cancel();

    const reduced = window.matchMedia(REDUCED_MOTION).matches;

    if (!animate || reduced) {
      this.#panel.hidden = !open;
      this.#panel.style.height = '';
      return;
    }

    if (open) {
      this.#panel.hidden = false;
      const target = this.#panel.scrollHeight;
      this.#animation = this.#panel.animate(
        [
          { height: '0px', opacity: 0 },
          { height: `${target}px`, opacity: 1 },
        ],
        { duration: DURATION, easing: EASING }
      );
      // Release the pinned height so the panel reflows with its content later.
      this.#animation.onfinish = () => {
        this.#panel.style.height = '';
      };
      return;
    }

    const from = this.#panel.scrollHeight;
    this.#animation = this.#panel.animate(
      [
        { height: `${from}px`, opacity: 1 },
        { height: '0px', opacity: 0 },
      ],
      { duration: DURATION, easing: EASING }
    );
    this.#animation.onfinish = () => {
      this.#panel.hidden = true;
      this.#panel.style.height = '';
    };
  }
}

if (!customElements.get('lh-accordion-item')) {
  customElements.define('lh-accordion-item', AccordionItem);
}

})();
